<template>
  <div ref="popupContainerEl">
    <div ref="referenceEl" data-testid="popup-trigger" @click="togglePopup">
      <!-- Default slot for trigger -->
      <slot />
    </div>
    <Teleport to="body" :disabled="!teleport">
      <Transition name="fade">
        <div
          ref="floatingEl"
          v-if="!disabled && shouldShowPopup"
          class="z-[9999]"
          data-testid="popup-content"
          :style="floatingStyles"
          @click="!interactive && closePopup()"
        >
          <slot name="content" :close="closePopup" />
        </div>
      </Transition>
    </Teleport>
  </div>
</template>

<script lang="ts" setup>
import { computed, onMounted, onUnmounted, ref, Ref, toRefs, watchEffect } from 'vue';
import { useFloating, autoUpdate, Placement, offset, flip, shift } from '@floating-ui/vue';
import { useClickAway } from '../composables';

const popupContainerEl: Ref<HTMLElement | null> = ref(null);
const referenceEl: Ref<HTMLElement | null> = ref(null);
const floatingEl: Ref<HTMLElement | null> = ref(null);

const emit = defineEmits(['open:popup', 'close:popup']);
const props = withDefaults(
  defineProps<{
    disabled?: boolean;
    placement?: Placement;
    disableClickAway?: boolean;
    escapeKey?: boolean;
    // legacy prop name (don't close while clicking inside popup)
    interactive?: boolean;
    offsetSize?: number;
    /** Gap kept from the screen edge when the popup is pushed back into view. */
    edgePadding?: number;
    /** Render popup content in document.body to escape clipping ancestors. */
    teleport?: boolean;
  }>(),
  {
    disabled: false,
    placement: 'bottom-start',
    escapeKey: true,
    disableClickAway: false,
    interactive: true,
    offsetSize: 12,
    edgePadding: 0,
  }
);
const shouldShowPopup = ref(false);
const enableClickAway = computed(() => !disableClickAway.value);

const { disabled, placement, disableClickAway, offsetSize, edgePadding } = toRefs(props);

const middleware = [offset(offsetSize.value)];
const flipMiddleware = flip({
  // Ensure we flip to the perpendicular axis if it doesn't fit
  // on narrow viewports.
  crossAxis: 'alignment',
  fallbackAxisSideDirection: 'end',
});
const shiftMiddleware = shift({ padding: edgePadding.value });

// Prioritize flip over shift for edge-aligned placements only.
if (placement.value.includes('-')) {
  middleware.push(flipMiddleware, shiftMiddleware);
} else {
  middleware.push(shiftMiddleware, flipMiddleware);
}

const { floatingStyles } = useFloating(referenceEl, floatingEl, {
  placement,
  middleware,
  whileElementsMounted: autoUpdate,
  open: shouldShowPopup,
});

/**
 * Open popup.
 */
const openPopup = () => {
  shouldShowPopup.value = true;
  emit('open:popup');
};
/**
 * Close popup.
 */
const closePopup = () => {
  shouldShowPopup.value = false;
  emit('close:popup');
};
/**
 * Toggle popup visibility.
 */
const togglePopup = () => {
  if (shouldShowPopup.value) {
    closePopup();
  } else {
    openPopup();
  }
};
/**
 * Close popup when clicking away.
 * @param event
 */
const handleClickAway = (event: Event) => {
  if (floatingEl.value && (event.target === floatingEl.value || event.composedPath().includes(floatingEl.value))) {
    return;
  }

  if (shouldShowPopup.value) {
    closePopup();
  }
};
/**
 * Handle keydown event
 */
const onKeyPress = (event: KeyboardEvent) => {
  if (shouldShowPopup.value && event.key === 'Escape' && props.escapeKey) {
    closePopup();
  }
};

onMounted(() => {
  document.addEventListener('keydown', onKeyPress);
});

onUnmounted(() => {
  document.removeEventListener('keydown', onKeyPress);
});

/**
 * Expose the public API.
 */
defineExpose({ openPopup, closePopup, togglePopup });

/**
 * Watch for click away.
 */
watchEffect(() => {
  if (enableClickAway.value) {
    useClickAway(popupContainerEl, handleClickAway);
  }
});
</script>
